import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { toast } from "sonner";
import { useMutation } from "urql";

import { StreamingChannelForm } from "@/components/forms/StreamingChannelForm";
import { StreamingChannelInput } from "@/components/forms/validation/streaming-channel";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { cn } from "@/utils";

const createStreamingChannelMutation = graphql(`
    mutation createStreamingChannel($input: CreateStreamingChannelInput!) {
        createStreamingChannel(input: $input) {
            id
            externalChannelHandle
            externalChannelId
            provider
            imageId
            createdAt
            updatedAt
        }
    }
`);

export const StreamingChannelCreateDialog = ({ onSuccess }: { onSuccess?: () => void }) => {
    const [_res, createStreamingChannel] = useMutation(createStreamingChannelMutation);
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit: SubmitHandler<StreamingChannelInput> = async input => {
        setIsSubmitting(true);
        const result = await createStreamingChannel({
            input: {
                externalChannelHandle: input.externalChannelHandle,
                externalChannelId: input.externalChannelId,
                provider: input.provider,
                imageId: input.imageId,
            },
        });

        setIsSubmitting(false);

        if (result.error) {
            toast.error("Unable to create streaming channel", {
                description: result.error.message,
            });
        } else {
            toast.success("Streaming channel was created");
            setIsOpen(false);
            onSuccess?.();
        }
    };

    const defaultValues: StreamingChannelInput = {
        externalChannelHandle: "",
        externalChannelId: "",
        provider: "YOUTUBE",
        imageId: null,
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PlusIcon />
                Create Streaming Channel
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-2xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <DialogTitle>Create Streaming Channel</DialogTitle>
                </DialogHeader>
                <StreamingChannelForm
                    onSubmit={onSubmit}
                    defaultValues={defaultValues}
                    isSubmitting={isSubmitting}
                />
            </DialogContent>
        </Dialog>
    );
};
